/**
 * TrainingCatalogPage.jsx
 * =======================
 * Page d'index des modules de formation pour une population donnée (client | staff).
 * Affiche les modules par catégorie, avec badge "Disponible" ou "Bientôt".
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Clock, Award, ChevronRight, Lock, BarChart3, Truck, FileText,
  Zap, DollarSign, ClipboardList, Users, Shield, ArrowLeft, CheckCircle2, Play, RotateCcw,
  Download,
} from 'lucide-react';
import { getCatalog } from '../components/training/registry';
import { getPopulationStats, getModuleProgress, getAllProgress } from '../components/training/progressService';

const TRAINING_DOC_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/docs/training`;

const ICON_MAP = {
  BarChart3, Truck, FileText, Zap, DollarSign, ClipboardList, Users, Shield, Award, BookOpen,
};

const LEVEL_BADGE = {
  Débutant: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Débutant → Intermédiaire': 'bg-blue-100 text-blue-700 border-blue-200',
  Intermédiaire: 'bg-amber-100 text-amber-700 border-amber-200',
  'Intermédiaire → Avancé': 'bg-orange-100 text-orange-700 border-orange-200',
  Avancé: 'bg-red-100 text-red-700 border-red-200',
};

const TrainingCatalogPage = ({ population = 'client' }) => {
  const navigate = useNavigate();
  const catalog = useMemo(() => getCatalog(population), [population]);
  // Force re-read sur retour de module (event focus)
  const [progressTick, setProgressTick] = useState(0);
  useEffect(() => {
    const onFocus = () => setProgressTick((t) => t + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  const stats = useMemo(() => getPopulationStats(catalog), [catalog, progressTick]);
  const allProgress = useMemo(() => getAllProgress(), [progressTick]);

  const grouped = useMemo(() => {
    const map = {};
    catalog.forEach((m) => {
      const cat = m.category || 'Autre';
      (map[cat] = map[cat] || []).push(m);
    });
    return map;
  }, [catalog]);

  const totalModules = catalog.length;
  const availableCount = catalog.filter((m) => m.available).length;
  const themeAccent = population === 'staff' ? 'indigo' : 'emerald';
  const headerBg =
    population === 'staff'
      ? 'from-indigo-50 via-white to-slate-50'
      : 'from-emerald-50 via-white to-teal-50';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className={`bg-gradient-to-br ${headerBg} border-b border-slate-200`}>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4"
            data-testid="training-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className={`w-5 h-5 text-${themeAccent}-600`} />
            <p className={`text-xs uppercase tracking-[0.3em] font-semibold text-${themeAccent}-600`}>
              Centre de formation {population === 'staff' ? 'interne' : 'client'}
            </p>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Maîtriser Baobab Connect
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mb-6">
            {population === 'staff'
              ? 'Programme complet de formation pour l\'équipe Baobab : 14 modules couvrant toutes les fonctionnalités opérationnelles, commerciales et financières.'
              : 'Programme complet de formation client : 10 modules pour exploiter à 100 % toutes les fonctionnalités à votre disposition.'}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-${themeAccent}-100 text-${themeAccent}-700 text-sm font-medium`}>
              <Award className="w-4 h-4" /> {availableCount} module{availableCount > 1 ? 's' : ''} disponible{availableCount > 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm">
              <Clock className="w-4 h-4" /> {totalModules} modules au total
            </span>
          </div>

          {/* Barre de progression utilisateur + badges */}
          <div className="mt-6 p-5 bg-white/70 backdrop-blur rounded-2xl border border-slate-200" data-testid="user-progress-bar">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Votre progression</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.completedCount} / {stats.totalModules} <span className="text-base font-normal text-slate-500">modules complétés</span>
                </p>
              </div>
              <div className={`text-4xl font-extrabold text-${themeAccent}-600`}>{stats.percent}%</div>
            </div>
            <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r from-${themeAccent}-400 to-${themeAccent}-600 transition-all duration-700`}
                style={{ width: `${stats.percent}%` }}
              />
            </div>
            {stats.badges.length > 0 && (
              <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="user-badges">
                <span className="text-xs font-semibold text-slate-600 mr-1">Badges débloqués :</span>
                {stats.badges.map((b) => (
                  <span key={b.id} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-${themeAccent}-50 border border-${themeAccent}-200 text-${themeAccent}-700 text-sm font-medium`}>
                    <span className="text-base">{b.emoji}</span> {b.label}
                  </span>
                ))}
              </div>
            )}
            {stats.startedCount > 0 && (
              <p className="text-xs text-slate-500 mt-3">
                <Play className="inline w-3 h-3 mr-1" /> {stats.startedCount} module{stats.startedCount > 1 ? 's' : ''} en cours
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Modules par catégorie */}
      <section className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {Object.entries(grouped).map(([category, modules]) => (
          <div key={category}>
            <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-500 mb-4">{category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {modules.map((mod) => {
                const Icon = ICON_MAP[mod.icon] || BookOpen;
                const isAvailable = mod.available;
                const prog = allProgress[mod.id];
                const isCompleted = !!prog?.completedAt;
                const isStarted = !!prog && !isCompleted;
                return (
                  <div
                    key={mod.id}
                    role={isAvailable ? 'button' : undefined}
                    tabIndex={isAvailable ? 0 : -1}
                    onClick={() =>
                      isAvailable && navigate(`/docs/${population}/training/${mod.id}`)
                    }
                    onKeyDown={(e) => {
                      if (isAvailable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        navigate(`/docs/${population}/training/${mod.id}`);
                      }
                    }}
                    className={`group text-left bg-white rounded-2xl border ${isCompleted ? `border-${themeAccent}-400 ring-1 ring-${themeAccent}-200` : 'border-slate-200'} p-5 transition-all ${
                      isAvailable
                        ? 'cursor-pointer hover:border-slate-400 hover:shadow-md'
                        : 'opacity-60 cursor-not-allowed'
                    }`}
                    data-testid={`training-module-${mod.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex-shrink-0 w-12 h-12 rounded-xl ${isCompleted ? `bg-${themeAccent}-600 text-white` : `bg-${themeAccent}-100 text-${themeAccent}-600`} flex items-center justify-center relative`}
                      >
                        <Icon className="w-6 h-6" />
                        {isCompleted && (
                          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow">
                            <CheckCircle2 className={`w-4 h-4 text-${themeAccent}-600`} />
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-400">#{String(mod.order).padStart(2, '0')}</span>
                          {!isAvailable && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              <Lock className="w-3 h-3" /> Bientôt
                            </span>
                          )}
                          {isCompleted && (
                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-${themeAccent}-700 bg-${themeAccent}-100 px-2 py-0.5 rounded`}>
                              <CheckCircle2 className="w-3 h-3" /> Complété
                            </span>
                          )}
                          {isStarted && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                              <Play className="w-3 h-3" /> En cours
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-slate-900 text-lg leading-tight mb-1">{mod.title}</h3>
                        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {mod.duration}</span>
                          <span className={`px-2 py-0.5 rounded border ${LEVEL_BADGE[mod.level] || LEVEL_BADGE.Débutant}`}>{mod.level}</span>
                        </div>
                      </div>
                      {isAvailable && (
                        <ChevronRight className={`w-5 h-5 text-slate-300 group-hover:text-${themeAccent}-600 group-hover:translate-x-0.5 transition-all`} />
                      )}
                    </div>
                    {isAvailable && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Offline</span>
                        <a
                          href={`${TRAINING_DOC_BASE}/${mod.id}.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-${themeAccent}-700`}
                          data-testid={`download-pdf-${mod.id}`}
                        >
                          <Download className="w-3 h-3" /> PDF
                        </a>
                        <a
                          href={`${TRAINING_DOC_BASE}/${mod.id}.pptx`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-${themeAccent}-700`}
                          data-testid={`download-pptx-${mod.id}`}
                        >
                          <Download className="w-3 h-3" /> PPTX
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default TrainingCatalogPage;
